import moment from "moment";
import React, { useState } from "react";
import { ListGroup } from "react-bootstrap";
import { ICON } from "../../../assets";
import { navigate } from "../../../routes";
import { admin } from "../../../routes/admin";
import sdk from "../../../sdk/sdk";
import { formatDateToFrench, price } from "../../../utils";
import { useAuth } from "../../Auth/useAuth";
import { useVrpRedirect } from "../../Auth/useVrpRedirect";
import BasketBuilder from "../../Basket/BasketBuilder/BasketBuilder";
import ApplicationHeader from "../../Headers/ApplicationHeader/ApplicationHeader";
import useTranslations from "../../Translations/useTranslations";
import UiButton, { UI_BUTTON_TYPE } from "../../ui/UiButton";
import UiIcon from "../../ui/UiIcon";
import { useCart } from "./../useCart";
import "./ViewCartPage.css";
import { registerController } from "../../../requestRegistry";

function ViewCartPage() {
  const [{ user }] = useAuth();
  useVrpRedirect();
  const trl = useTranslations();
  const [
    carts,
    { removeCart, selectCart, getCartDataByID, removeAllCart, replaceCartId, flushSyncCartToDB },
  ] = useCart(user.client_profile?.id);
  const [cartsCreating, setCartsCreating] = useState(false);
  function renderCart({ cart }) {
    const _deleteCartById = async (_id) => {
      if (!window.confirm(trl.message("HomePage.Dialog.deleteCart"))) return;
      setCartsCreating(true);
      try {
        await removeCart({ creator: user.client_profile?.id, _id });
      } finally {
        setCartsCreating(false);
      }
    };

    const _navigateToCartDetail = (_id) => {
      const cart = carts.carts.find((c) => c._id === _id);

      // Flush any pending cart saves before navigation
      if (flushSyncCartToDB) {
        flushSyncCartToDB();
      }

      setCartsCreating(true);
      const controller = new AbortController();
      const unregister = registerController(controller);
      sdk.orders
        .checkAndSavecartbyIdDB(user.client_profile?.id, _id, cart, controller.signal)
        .then((data) => {
          unregister();
          if (data.payload.header.id != _id) {
            replaceCartId(_id, data.payload.header.id, user.client_profile?.id);
            _id = data.payload.header.id;
          }
          selectCart({ creator: user.client_profile?.id, _id });
          navigate.push(admin.APP_NAVIGATION, { critere: "start" });
        })
        .catch((err) => {
          unregister();
          // Silent: user clicked "Revenir à mon compte" and aborted the request
          if (err?.name === "AbortError") return;
          alert(
            "Erreur lors de l'ouverture du panier. Vous pouvez réessayer ou supprimer et créer un nouveau panier."
          );
        })
        .finally(() => setCartsCreating(false));
    };

    const getCartpriceByID = (cartId) => {
      if (!cartId) return 0;
      const cart = carts.carts.find((c) => c._id === cartId);
      if (!cart) return 0;
      const { mt_total, mt_total_remise_pack, cumul_plv_in_cart } =
        getCartDataByID(cart._id);
      return price(mt_total - mt_total_remise_pack - cumul_plv_in_cart) + " €";
    };

    return (
      <React.Fragment>
        <div key={cart._id} className="view-card-list-item">
          <div className="view-card-info">
            <span>{formatDateToFrench(new Date(cart.created_at))}</span>
            <span>{getCartpriceByID(cart._id)}</span>
          </div>
          <div className="view-card-controls">
            <UiButton
              disabled={cartsCreating}
              classNames={["view-card-list-item-button"]}
              onClick={() => _navigateToCartDetail(cart._id)}
              content={
                <React.Fragment>
                  <span className="view-card-button-label">Reprendre</span>
                </React.Fragment>
              }
            />
            <UiIcon
              disabled={cartsCreating}
              icon={ICON.trashIcon}
              onClick={() => !cartsCreating && _deleteCartById(cart._id)}
              alt="Supprimer le panier"
              size="lg"
            />
          </div>
        </div>
      </React.Fragment>
    );
  }

  function renderCarts() {
    const getItems = carts?.carts?.sort((item1, item2) => {
      const item1Date = moment(item1.created_at);
      const item2Date = moment(item2.created_at);
      // Order by datetime desc
      if (item1Date.isAfter(item2Date)) return -1;
      if (item2Date.isAfter(item1Date)) return 1;
      return 0;
    });
    if (!getItems || getItems.length === 0) {
      return (
        <React.Fragment>
          <ListGroup variant="flush" className="cart-list ">
            <ListGroup.Item style={{ textAlign: "center", margin: "20px 0" }}>
              Aucun panier disponible
            </ListGroup.Item>
          </ListGroup>
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <div className="view-cart-list-container">
          {getItems.map((cart) => (
            <div key={cart._id}>{renderCart({ cart })}</div>
          ))}
        </div>
      </React.Fragment>
    );
  }

  function renderClearAllCarts() {
    const _deleteAllCarts = async () => {
      if (!window.confirm("Do you really want to delete all baskets?")) return;
      setCartsCreating(true);
      try {
        await removeAllCart({ creator: user.client_profile?.id });
      } finally {
        setCartsCreating(false);
      }
    };
    if (carts?.carts?.length <= 1) return null;

    return (
      <React.Fragment>
        <UiButton
          classNames={["cart-lists-delete-button"]}
          buttonUiType={[UI_BUTTON_TYPE.ONLY_BORDER]}
          onClick={_deleteAllCarts}
          content="Supprimer tous les paniers"
          disabled={cartsCreating}
        />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <ApplicationHeader headerConfig={{ showcart: true }} />
      <div className="view-cart-page">
        <div className="view-cart-header">Mes paniers en attente</div>
        <div className="view-cart-content">
          {renderCarts()}
          <div className="view-cart-button-group">
            <BasketBuilder isCartCreating={setCartsCreating} disabled={cartsCreating} />
            {renderClearAllCarts()}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default ViewCartPage;
